import { useState } from 'react';
import { Link } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/auth-context';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Eye, EyeOff, Building2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { AuthPageLayout } from '@/components/auth/auth-page-layout';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { CustomerAccess } from '@/components/auth/customer-access';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { t } = useTranslation('login');
  const { login, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setLoginError(null);
      clearError();

      await login(data as { email: string; password: string });

      toast({
        title: t('toasts.success'),
        description: t('toasts.successDescription'),
      });
    } catch (err: any) {
      let errorMessage: string;

      if (err.response?.status === 401) {
        errorMessage = t('toasts.invalidCredentials');
      } else {
        errorMessage = err.response?.data?.error?.message ||
                      err.response?.data?.message ||
                      t('toasts.error');
      }

      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const emailField = register('email');
  const passwordField = register('password');
  const clearLoginError = () => {
    if (loginError) setLoginError(null);
  };

  return (
    <AuthPageLayout align="left" title={t('chooser.title')} subtitle={t('chooser.subtitle')}>
      {/*
        Customer first in the DOM: below lg it renders as a strip above the
        form, so the route customers need is visible without scrolling. At lg
        it moves to the narrower second column and the form takes the wider
        first one.
      */}
      <div className="grid gap-6 lg:grid-cols-[1.45fr_1fr] lg:items-start">
        <CustomerAccess className="lg:order-2" />

        <div className="rounded-2xl border border-line bg-surface shadow-card lg:order-1">
          <div className="flex items-center gap-3 border-b border-line p-5 sm:px-[22px]">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-500">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-h3 text-ink">{t('chooser.business.title')}</h2>
              <p className="mt-0.5 text-body-xs text-soft">
                {t('chooser.business.description')}
              </p>
            </div>
          </div>

          <div className="p-5 sm:p-[22px]">
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {loginError && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-md bg-danger-soft p-4"
                >
                  <AlertCircle className="h-5 w-5 flex-shrink-0 text-danger" aria-hidden="true" />
                  <p className="text-body-sm font-medium text-danger-text">{loginError}</p>
                </div>
              )}

              <Input
                {...emailField}
                onChange={(e) => {
                  emailField.onChange(e);
                  clearLoginError();
                }}
                type="email"
                inputSize="lg"
                autoComplete="email"
                label={t('form.emailLabel')}
                placeholder={t('form.emailPlaceholder')}
                error={errors.email?.message}
              />

              <Input
                {...passwordField}
                onChange={(e) => {
                  passwordField.onChange(e);
                  clearLoginError();
                }}
                type={showPassword ? 'text' : 'password'}
                inputSize="lg"
                autoComplete="current-password"
                label={t('form.passwordLabel')}
                placeholder={t('form.passwordPlaceholder')}
                error={errors.password?.message}
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('form.hidePassword') : t('form.showPassword')}
                    className="text-soft transition-colors hover:text-ink"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                }
              />

              <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2.5 text-body-sm text-ink">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-[18px] w-[18px] rounded border-line-strong text-brand-500 focus:ring-brand-500"
                  />
                  {t('form.rememberMe')}
                </label>

                <Link
                  href="/forgot-password"
                  className="text-body-sm font-medium text-brand-500 hover:text-brand-700"
                >
                  {t('form.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                loading={isLoading}
                disabled={isLoading}
              >
                {t('chooser.business.signInButton')}
              </Button>
            </form>

            <div className="mt-4">
              <GoogleSignInButton flow="login" />
            </div>

            <p className="mt-4 text-body-sm text-soft">
              {t('chooser.business.newHere')}{' '}
              <Link
                href="/register"
                className="font-medium text-brand-500 hover:text-brand-700"
              >
                {t('chooser.business.createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
}
